import { Component, Inject, Optional, Input, Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AddQuickBuildModalComponent } from '../../../people/filters/add-quick-build-modal/add-quick-build-modal.component';
import {
  StoredQuickBuild,
  QuickBuildStorageService,
} from '../../../core/services/quick-build-storage.service';
import { NotificationService } from '../../notification-service/notification.service';
import { AuthService } from '../../../auth/service/auth.service';
import { BaseItemRendererComponent } from './base-item-renderer.component';
import { DarkModeService } from '../../dark-mode/dark-mode.service';

@Component({
    selector: 'app-quick-build-item-renderer',
    templateUrl: './quick-build-item-renderer.component.html',
    styleUrls: ['./quick-build-item-renderer.component.scss'],
    standalone: false
})
export class QuickBuildItemRendererComponent extends BaseItemRendererComponent<StoredQuickBuild> {
  protected storageService: QuickBuildStorageService;

  // Support for both injected and input-based usage
  @Input() override item!: StoredQuickBuild;
  @Input() override allowDelete!: boolean;
  @Input() override showCreator!: boolean;
  @Output() itemSelected = new EventEmitter<StoredQuickBuild>();

  constructor(
    @Optional() @Inject('item') injectedItem: StoredQuickBuild | null,
    @Optional() @Inject('allowDelete') injectedAllowDelete: boolean | null,
    @Optional() @Inject('showCreator') injectedShowCreator: boolean | null,
    @Optional() @Inject('onItemSelected') onItemSelected: ((item: StoredQuickBuild) => void) | null,
    @Optional() @Inject('onItemDeleted') onItemDeleted: ((item: StoredQuickBuild) => void) | null,
    quickBuildStorageService: QuickBuildStorageService,
    dialog: MatDialog,
    notificationService: NotificationService,
    authService: AuthService,
    public darkModeService: DarkModeService
  ) {
    // Create default values for when used as Input component
    const defaultItem = {} as StoredQuickBuild;
    const defaultAllowDelete = false;
    const defaultShowCreator = false;
    const defaultOnItemSelected = () => {};
    const defaultOnItemDeleted = () => {};

    super(
      injectedItem || defaultItem,
      injectedAllowDelete ?? defaultAllowDelete,
      injectedShowCreator ?? defaultShowCreator,
      onItemSelected || defaultOnItemSelected,
      onItemDeleted || defaultOnItemDeleted,
      dialog,
      notificationService,
      authService
    );
    this.storageService = quickBuildStorageService;
  }

  protected getItemTypeName(): string {
    return 'QuickBuild';
  }

  // Override selectItem to support both injection-based and input-based usage
  override selectItem() {
    const currentItem = this.item;
    if (currentItem) {
      // Only call super.selectItem() if there's an injected callback
      try {
        super.selectItem();
      } catch (e) {
        // Ignore errors from super.selectItem() when used as input component
      }
      this.itemSelected.emit(currentItem);
    }
  }

  // Getter for template access to current item
  get currentItem(): StoredQuickBuild {
    return this.item;
  }

  // Getter for template access to allowDelete
  get canDelete(): boolean {
    return this.allowDelete;
  }

  // Getter for template access to showCreator
  get shouldShowCreator(): boolean {
    return this.showCreator;
  }

  editItem(event: Event) {
    event.stopPropagation();
    
    const currentItem = this.item;
    if (!currentItem) return;

    const dialogRef = this.dialog.open(AddQuickBuildModalComponent, {
      width: '600px',
      data: {
        quickBuild: currentItem,
        editMode: true
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Update the quickbuild with the new name, description, and category
        const updatedQuickBuild: StoredQuickBuild = {
          ...currentItem,
          name: result.name,
          description: result.description,
          category: result.category
        };

        // Save the updated quickbuild
        this.storageService.update(currentItem.id, updatedQuickBuild).subscribe();
      }
    });
  }
}
